import { useEffect, useState } from 'react';
import { AppShell } from '../../components/app/AppShell';
import { Card } from '../../components/ui/Card';
import { api, ApiError } from '../../lib/api';
import { downloadCsv } from '../../lib/csvExport';
import type { AuditEvent, Me, RolesOverviewMember } from '../../lib/types';

const navItems = [
  { label: 'Карта', to: '/app' },
  { label: 'Реквизиты', to: '/app/organization' },
  { label: 'Участники', to: '/app/organization' },
  { label: 'Роли и группы', to: '/app/organization' },
  { label: 'Журнал действий', to: '/app/organization/audit-log' },
];

const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 500;

function initialsOf(me: Me | null): string {
  if (!me) return '…';
  const a = me.first_name?.[0] ?? '';
  const b = me.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || '??';
}

function memberLabel(m: RolesOverviewMember): string {
  const full = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return full || m.email || '—';
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function AuditLogPage() {
  const [me, setMe] = useState<Me | null>(null);
  const [members, setMembers] = useState<RolesOverviewMember[]>([]);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [actorUserId, setActorUserId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
    api.getRolesOverview().then((o) => setMembers(o.members)).catch(() => {});
  }, []);

  const filterParams = () => ({
    actor_user_id: actorUserId || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo ? `${dateTo}T23:59:59` : undefined,
    sort,
  });

  const load = async (nextOffset: number) => {
    setLoading(true);
    setError(null);
    try {
      const page = await api.getAuditLog({ ...filterParams(), limit: PAGE_SIZE, offset: nextOffset });
      setEvents((prev) => (nextOffset === 0 ? page : [...prev, ...page]));
      setHasMore(page.length === PAGE_SIZE);
      setOffset(nextOffset);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить журнал действий.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorUserId, dateFrom, dateTo, sort]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const all: AuditEvent[] = [];
      let pageOffset = 0;
      for (;;) {
        const page = await api.getAuditLog({ ...filterParams(), limit: EXPORT_PAGE_SIZE, offset: pageOffset });
        all.push(...page);
        if (page.length < EXPORT_PAGE_SIZE) break;
        pageOffset += EXPORT_PAGE_SIZE;
      }
      downloadCsv(
        `audit-log-${new Date().toISOString().slice(0, 10)}.csv`,
        ['Дата и время', 'Действие', 'Пользователь'],
        all.map((e) => [formatWhen(e.occurred_at), e.action, e.actor_name]),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось выгрузить журнал.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppShell
      orgLabel={me?.workspace?.organization_name ?? '—'}
      navItems={navItems}
      backLabel="Вернуться на карту"
      backTo="/app"
      title="Журнал действий"
      breadcrumb="Личный кабинет / Организация / Журнал действий"
      userInitials={initialsOf(me)}
      userAvatarUrl={me?.avatar_url}
    >
      <div className="mx-auto max-w-[1100px]">
        <Card
          title="Действия участников"
          action={
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="flex h-9 items-center rounded-[9px] border border-border-default bg-bg-surface px-3 text-[12px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? 'Выгружаем…' : 'Экспорт в Excel'}
            </button>
          }
        >
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-medium text-text-secondary">Пользователь</label>
              <select
                value={actorUserId}
                onChange={(e) => setActorUserId(e.target.value)}
                className="h-9 min-w-[200px] rounded-[9px] border border-border-default bg-bg-surface px-2 text-[12px] text-text-primary outline-none"
              >
                <option value="">Все пользователи</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {memberLabel(m)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-medium text-text-secondary">С даты</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 rounded-[9px] border border-border-default bg-bg-surface px-2 text-[12px] text-text-primary outline-none"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-medium text-text-secondary">По дату</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 rounded-[9px] border border-border-default bg-bg-surface px-2 text-[12px] text-text-primary outline-none"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-medium text-text-secondary">Сортировка</label>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as 'asc' | 'desc')}
                className="h-9 rounded-[9px] border border-border-default bg-bg-surface px-2 text-[12px] text-text-primary outline-none"
              >
                <option value="desc">Сначала новые</option>
                <option value="asc">Сначала старые</option>
              </select>
            </div>
            {(actorUserId || dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => {
                  setActorUserId('');
                  setDateFrom('');
                  setDateTo('');
                }}
                className="h-9 text-[11px] font-medium text-text-secondary hover:text-text-primary"
              >
                Сбросить фильтры
              </button>
            )}
          </div>

          {error && <p className="mt-4 text-xs font-medium text-status-warning">{error}</p>}

          <table className="mt-5 w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-border-default">
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-text-secondary">Дата и время</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-text-secondary">Действие</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-text-secondary">Пользователь</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {events.length === 0 && !loading && (
                <tr>
                  <td colSpan={3} className="py-6 text-center text-xs text-text-secondary">
                    Событий не найдено
                  </td>
                </tr>
              )}
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="px-3 py-2.5 text-[11px] text-text-secondary">{formatWhen(e.occurred_at)}</td>
                  <td className="px-3 py-2.5 text-xs font-medium text-text-primary">{e.action}</td>
                  <td className="px-3 py-2.5 text-[11px] text-text-secondary">{e.actor_name}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {hasMore && (
            <button
              type="button"
              onClick={() => load(offset + PAGE_SIZE)}
              disabled={loading}
              className="mt-4 flex h-9 w-full items-center justify-center rounded-[9px] border border-border-default bg-bg-surface text-[12px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Загружаем…' : 'Показать ещё'}
            </button>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
